import React from 'react';
import { useParams } from 'react-router-dom';
import { Container, Kicker } from '../components/ui/Primitives';
import { NotFound } from './NotFound';
interface LegalDocument {
  title: string;
  updated: string;
  intro: string;
  sections: {
    heading: string;
    paragraphs: string[];
  }[];
}
const DOCUMENTS: Record<string, LegalDocument> = {
  privacy: {
    title: 'Privacy notice',
    updated: '1 July 2026',
    intro: 'This notice explains what personal data Cavalla Solution collects, why we collect it, how long we keep it and the rights you hold over it.',
    sections: [{
      heading: 'Who we are',
      paragraphs: ['Cavalla Solution is the data controller for the personal data described in this notice. You can reach us at hello@cavallasolution.com.']
    }, {
      heading: 'What we collect',
      paragraphs: ['When you subscribe, download a resource, complete the assessment or contact us, we collect your name, work email, organisation, role and sector, together with the action that prompted the submission.', 'When you use the client portal we hold your account details, the organisation you belong to, and a log of the deliverables you download.', 'We collect analytics data about how the site is used, but only after you accept analytics cookies.']
    }, {
      heading: 'Why we collect it',
      paragraphs: ['To deliver the material you asked for, to respond to your enquiry, to run the client portal, and where you have consented, to send you insights and updates. Our lawful basis is consent for marketing, contract for portal services, and legitimate interests for responding to a business enquiry.']
    }, {
      heading: 'Where your data is held',
      paragraphs: ['Personal data is held in the European Union. Our customer relationship platform runs on its European instance, and our application database and file storage are located in the European Union.']
    }, {
      heading: 'How long we keep it',
      paragraphs: ['Marketing contacts are kept until you unsubscribe or ask us to delete you, and are reviewed every two years. Portal records are kept for the duration of the engagement and for six years afterwards where required for accounting.']
    }, {
      heading: 'Your rights',
      paragraphs: ['You can ask for a copy of your data, ask us to correct it, ask us to delete it, or object to its use for marketing. Write to hello@cavallasolution.com and we will respond within one month. You also have the right to complain to your data protection authority.']
    }]
  },
  terms: {
    title: 'Terms of use',
    updated: '1 July 2026',
    intro: 'These terms govern your use of the Cavalla Solution website. Consulting engagements are governed by a separate written agreement.',
    sections: [{
      heading: 'Use of this site',
      paragraphs: ['You may read, download and share our published material for your own or your organisation internal use. You may not republish it commercially without our written permission.']
    }, {
      heading: 'Not advice',
      paragraphs: ['The material on this site, including assessments and analysis, is general information. It is not financial, legal or investment advice, and Cavalla Solution is not a financial adviser. You should take your own professional advice before acting.']
    }, {
      heading: 'Proposals and fees',
      paragraphs: ['We do not publish fees on this website. Scope, timing and fees are set out in a written proposal following a discovery call, and that proposal is the only document that constitutes a quotation.']
    }, {
      heading: 'The client portal',
      paragraphs: ['Portal access is granted to named individuals at client organisations. You are responsible for keeping your credentials secure and for notifying us immediately if you believe an account has been compromised.']
    }, {
      heading: 'Liability',
      paragraphs: ['We take care to keep this site accurate but we do not warrant that it is free of error. To the extent permitted by law we exclude liability for loss arising from reliance on the website alone.']
    }]
  },
  cookies: {
    title: 'Cookie notice',
    updated: '1 July 2026',
    intro: 'We use a small number of cookies. Analytics and marketing cookies are only set once you accept them in the banner.',
    sections: [{
      heading: 'Essential cookies',
      paragraphs: ['These keep the site working. They hold your cookie choice, your portal session where you are signed in, and a token recording that you have already unlocked a gated article. They cannot be switched off.']
    }, {
      heading: 'Analytics cookies',
      paragraphs: ['These tell us which pages are read and how people move through the site. They are only set if you accept them, and the data is aggregated.']
    }, {
      heading: 'Marketing cookies',
      paragraphs: ['These allow us to attribute a subscription or download to the page that prompted it. They are only set if you accept them.']
    }, {
      heading: 'Changing your mind',
      paragraphs: ['Clear this site data in your browser and the banner will appear again on your next visit, letting you make a fresh choice.']
    }]
  }
};
export function Legal() {
  const {
    document: slug
  } = useParams<{
    document: string;
  }>();
  const doc = DOCUMENTS[slug ?? ''];
  if (!doc) return <NotFound data-id="element-803" data-mp="16r3owcioqfkt" />;
  return <>
      <section className="surface-dark" data-id="element-804" data-mp="16s4wu84emlyb">
        <Container className="py-12 sm:py-16" data-id="element-805" data-mp="16p0p50rqd82w">
          <Kicker data-id="element-806" data-mp="4egvs1r24gia">Legal</Kicker>
          <h1 className="mt-4 font-display text-[32px] leading-tight text-white sm:text-[42px]" data-id="element-807" data-mp="4fii52diwpfb">{doc.title}</h1>
          <p className="mt-4 text-small text-white/55" data-id="element-808" data-mp="9ao2hwra1w1a">Last updated {doc.updated}</p>
        </Container>
      </section>

      <Container className="py-14 sm:py-20" data-id="element-809" data-mp="i52glw02n0it">
        <div className="max-w-prose" data-id="element-810" data-mp="i5vq34qf044n">
          <p className="text-[17px] leading-relaxed text-body" data-id="element-811" data-mp="i6x1xbf98unf">{doc.intro}</p>
          {doc.sections.map(section => <section key={section.heading} data-id="element-812" data-mp="s3042ha3wtad">
              <h2 className="mb-4 mt-10 font-display text-[24px] leading-snug text-ink" data-id="element-813" data-mp="s1oycbx3pbgm">{section.heading}</h2>
              {section.paragraphs.map((paragraph, index) => <p key={index} className="mb-4 text-[16px] leading-[1.7] text-body" data-id="element-814" data-mp="11480suwz5m2c" data-mpi={index}>
                  {paragraph}
                </p>)}
            </section>)}
        </div>
      </Container>
    </>;
}